import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getCase, getDocuments, uploadDocuments, deleteDocument, analyzeCase, getExhibits, getReportUrl,
  updateDocument, bulkUpdateDocuments, bulkDeleteDocuments, getCollaborators, inviteCollaborator,
  removeCollaborator, updateCollaboratorRole, assignExhibitLabel, removeExhibitLabel,
  getFinalExhibits, getFinalExhibitListUrl, getLabeledDocumentUrl,
  getAiIdentificationReportUrl
} from '../lib/api';
import { useActivityTracking, ActivityType } from '../lib/activityTracking';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import Layout from '../components/Layout';
import DocumentPreview from '../components/DocumentPreview';
import AnalysisComparison from '../components/AnalysisComparison';
import ArgumentTemplates from '../components/ArgumentTemplates';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileText, Image as ImageIcon, FileType, Sparkles, Download, Trash2, MoreVertical, Tag, CheckCircle2, Loader2, Edit, Users, UserPlus, Eye, Pencil, Crown, X, ListChecks, FileDown, Hash, GitCompare, Lightbulb, History, Stamp } from 'lucide-react';

const CATEGORIES = ['Contract', 'Correspondence', 'Financial', 'Medical', 'Witness Statement', 'Expert Report', 'Evidence', 'Pleading', 'Other'];
const EXHIBIT_PARTIES = ['Plaintiff', 'Defendant', 'Petitioner', 'Respondent', 'Appellant', 'Appellee', 'Government', 'Joint'];

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { trackActivity, trackCaseView, trackDocumentView, trackAIUsage } = useActivityTracking();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [exhibits, setExhibits] = useState([]);
  const [finalExhibits, setFinalExhibits] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [arguments_, setArguments] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [collabDialogOpen, setCollabDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editDocDialog, setEditDocDialog] = useState(null);
  const [editTags, setEditTags] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Bulk operations
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkTags, setBulkTags] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  
  // Collaboration
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);

  // Analysis Comparison & Templates
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);

  // Exhibit Labeling
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelDoc, setLabelDoc] = useState(null);
  const [labelParty, setLabelParty] = useState('Plaintiff');
  const [labelNumbering, setLabelNumbering] = useState('numeric');
  const [labelNumber, setLabelNumber] = useState('');
  const [stampLabel, setStampLabel] = useState(true);
  const [labelPosition, setLabelPosition] = useState('top-right');
  const [labeling, setLabeling] = useState(false);

  const LABEL_POSITIONS = [
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-center', label: 'Top Center' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-center', label: 'Bottom Center' },
    { value: 'bottom-right', label: 'Bottom Right' },
  ];

  const loadData = useCallback(async () => {
    try {
      const [caseRes, docsRes, exhibitsRes, collabRes, finalRes] = await Promise.all([
        getCase(id), getDocuments(id), getExhibits(id), getCollaborators(id), getFinalExhibits(id)
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data);
      setExhibits(exhibitsRes.data);
      setCollaborators(collabRes.data);
      setFinalExhibits(finalRes.data);
      setArguments(caseRes.data.arguments || '');
      
      // Track case view activity
      trackCaseView(caseRes.data.id, caseRes.data.title);
    } catch (error) {
      toast.error('Failed to load case data');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, trackCaseView]);

  useEffect(() => { loadData(); }, [loadData]);

  const canEdit = caseData?.my_role === 'owner' || caseData?.my_role === 'editor';
  const isOwner = caseData?.my_role === 'owner';

  const handleFileSelect = (e) => setSelectedFiles(Array.from(e.target.files));

  const handleUpload = async () => {
    if (selectedFiles.length === 0) { toast.error('Please select files'); return; }
    setUploading(true); setUploadProgress(0);
    try {
      const progressInterval = setInterval(() => setUploadProgress(prev => Math.min(prev + 10, 90)), 200);
      await uploadDocuments(id, selectedFiles);
      clearInterval(progressInterval); setUploadProgress(100);
      toast.success(`${selectedFiles.length} document(s) uploaded`);
      setSelectedFiles([]); setUploadDialogOpen(false); loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const handleAnalyze = async () => {
    if (!arguments_.trim()) { toast.error('Enter case arguments'); return; }
    if (documents.length === 0) { toast.error('Upload documents first'); return; }
    setAnalyzing(true);
    try {
      await analyzeCase(id, arguments_);
      toast.success('Analysis complete!'); setAnalyzeDialogOpen(false); loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Analysis failed'); }
    finally { setAnalyzing(false); }
  };

  const handleDeleteDocument = async (docId) => {
    try { await deleteDocument(id, docId); setDocuments(documents.filter(d => d.id !== docId)); toast.success('Document deleted'); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const handleUpdateDocument = async () => {
    if (!editDocDialog) return;
    try {
      const tags = editTags.split(',').map(t => t.trim()).filter(t => t);
      await updateDocument(id, editDocDialog.id, { tags, category: editCategory || null });
      toast.success('Document updated'); setEditDocDialog(null); loadData();
    } catch (error) { toast.error('Update failed'); }
  };

  const openEditDialog = (doc) => { setEditDocDialog(doc); setEditTags(doc.tags?.join(', ') || ''); setEditCategory(doc.category || ''); };

  // Bulk operations
  const toggleDocSelection = (docId) => {
    const newSet = new Set(selectedDocs);
    if (newSet.has(docId)) newSet.delete(docId); else newSet.add(docId);
    setSelectedDocs(newSet);
  };
  const selectAllDocs = () => { if (selectedDocs.size === documents.length) setSelectedDocs(new Set()); else setSelectedDocs(new Set(documents.map(d => d.id))); };
  const handleBulkUpdate = async () => {
    if (selectedDocs.size === 0) return;
    try {
      const tags = bulkTags ? bulkTags.split(',').map(t => t.trim()).filter(t => t) : null;
      await bulkUpdateDocuments(id, Array.from(selectedDocs), tags, bulkCategory || null);
      toast.success(`Updated ${selectedDocs.size} documents`);
      setBulkDialogOpen(false); setBulkTags(''); setBulkCategory(''); setSelectedDocs(new Set()); setBulkMode(false); loadData();
    } catch (error) { toast.error('Bulk update failed'); }
  };
  const handleBulkDelete = async () => {
    if (selectedDocs.size === 0) return;
    try {
      await bulkDeleteDocuments(id, Array.from(selectedDocs));
      toast.success(`Deleted ${selectedDocs.size} documents`);
      setBulkDeleteConfirm(false); setSelectedDocs(new Set()); setBulkMode(false); loadData();
    } catch (error) { toast.error('Bulk delete failed'); }
  };

  // Collaboration
  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Enter an email'); return; }
    setInviting(true);
    try {
      const res = await inviteCollaborator(id, inviteEmail, inviteRole);
      toast.success(res.data.email_sent ? 'Invitation sent with email!' : 'Invitation sent!');
      setInviteEmail(''); loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Invite failed'); }
    finally { setInviting(false); }
  };
  const handleRemoveCollaborator = async (userId) => { try { await removeCollaborator(id, userId); toast.success('Collaborator removed'); loadData(); } catch (error) { toast.error('Remove failed'); } };
  const handleUpdateRole = async (userId, newRole) => { try { await updateCollaboratorRole(id, userId, newRole); toast.success('Role updated'); loadData(); } catch (error) { toast.error('Update failed'); } };

  // Exhibit Labeling
  const openLabelDialog = (doc) => { 
    setLabelDoc(doc); 
    setLabelParty('Plaintiff'); 
    setLabelNumbering('numeric'); 
    setLabelNumber(''); 
    setStampLabel(doc.file_type === 'pdf' || doc.file_type === 'image');
    setLabelPosition('top-right');
    setLabelDialogOpen(true); 
  };
  const handleAssignLabel = async () => {
    if (!labelDoc) return;
    setLabeling(true);
    try {
      const canStamp = stampLabel && (labelDoc.file_type === 'pdf' || labelDoc.file_type === 'image');
      await assignExhibitLabel(id, labelDoc.id, labelParty, labelNumbering, labelNumber || null, canStamp, labelPosition);
      const message = canStamp ? 'Exhibit label assigned and stamped on document' : 'Exhibit label assigned';
      toast.success(message); 
      setLabelDialogOpen(false); 
      loadData();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to assign label'); 
    } finally {
      setLabeling(false);
    }
  };
  const handleRemoveLabel = async (docId) => {
    try { await removeExhibitLabel(id, docId); toast.success('Label removed'); loadData(); }
    catch (error) { toast.error('Failed to remove label'); }
  };

  const handleDownloadReport = () => {
    const token = localStorage.getItem('token');
    fetch(getReportUrl(id), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `exhibit_report.pdf`; a.click(); })
      .catch(() => toast.error('Download failed'));
  };

  const handleDownloadLabeledDocument = (docId, filename) => {
    const token = localStorage.getItem('token');
    fetch(getLabeledDocumentUrl(id, docId), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) {
          throw new Error('Not found');
        }
        return res.blob();
      })
      .then(blob => { 
        const a = document.createElement('a'); 
        a.href = URL.createObjectURL(blob); 
        a.download = `labeled_${filename}`; 
        a.click(); 
      })
      .catch(() => toast.error('Labeled document not available. Apply label with stamping first.'));
  };

  const handleDownloadAiIdentificationReport = () => {
    const token = localStorage.getItem('token');
    toast.loading('Generating AI Identification Report...');
    fetch(getAiIdentificationReportUrl(id), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        toast.dismiss();
        if (!res.ok) {
          if (res.status === 404) {
            toast.error('No AI analysis found. Run AI analysis first.');
          } else {
            toast.error('Failed to generate report');
          }
          throw new Error('Download failed');
        }
        return res.blob();
      })
      .then(blob => { 
        const a = document.createElement('a'); 
        a.href = URL.createObjectURL(blob); 
        a.download = `AI_Exhibit_Identification_Report_${caseData?.title?.replace(/[^a-zA-Z0-9]/g, '_')?.slice(0, 30) || 'case'}.pdf`; 
        a.click();
        toast.success('Report downloaded successfully');
      })
      .catch(() => {});
  };

  const handleDownloadFinalList = () => {
    const token = localStorage.getItem('token');
    fetch(getFinalExhibitListUrl(id), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `final_exhibit_list.pdf`; a.click(); })
      .catch(() => toast.error('Download failed'));
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'docx': return <FileType className="w-5 h-5 text-blue-500" />;
      case 'image': return <ImageIcon className="w-5 h-5 text-green-500" />;
      default: return <FileText className="w-5 h-5 text-muted-foreground" />;
    }
  };
  const formatFileSize = (bytes) => { if (bytes < 1024) return bytes + ' B'; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'; return (bytes / (1024 * 1024)).toFixed(1) + ' MB'; };
  const getRoleIcon = (role) => { switch (role) { case 'owner': return <Crown className="w-4 h-4 text-bronze" />; case 'editor': return <Pencil className="w-4 h-4 text-blue-500" />; case 'viewer': return <Eye className="w-4 h-4 text-muted-foreground" />; default: return null; } };

  if (loading) {
    return <Layout><div className="p-6 lg:p-8"><div className="h-8 w-48 bg-muted animate-pulse rounded mb-8" /><div className="grid gap-6">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />)}</div></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="case-detail-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/cases')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">{caseData?.title}</h1>
                {caseData?.case_number && <Badge variant="secondary" className="font-mono text-xs">{caseData.case_number}</Badge>}
                {caseData?.is_shared && <Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" />Shared</Badge>}
              </div>
              <p className="font-body text-muted-foreground mt-1">{caseData?.client_name} • {documents.length} docs • {finalExhibits.length} final exhibits</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="upload-docs-btn"><Upload className="w-4 h-4 mr-1" />Upload</Button></DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader><DialogTitle>Upload Documents</DialogTitle><DialogDescription>PDF, Word, or images (OCR)</DialogDescription></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-bronze/50 transition-colors">
                        <Input type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" onChange={handleFileSelect} className="hidden" id="file-upload" />
                        <label htmlFor="file-upload" className="cursor-pointer"><Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">Click to select</p></label>
                      </div>
                      {selectedFiles.length > 0 && <ScrollArea className="h-24">{selectedFiles.map((f,i) => <div key={i} className="flex items-center gap-2 text-sm py-1"><FileText className="w-4 h-4" /><span className="truncate">{f.name}</span><span className="ml-auto text-xs text-muted-foreground">{formatFileSize(f.size)}</span></div>)}</ScrollArea>}
                      {uploading && <Progress value={uploadProgress} className="h-2" />}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button><Button onClick={handleUpload} disabled={uploading || !selectedFiles.length} className="bg-navy hover:bg-navy-light">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={analyzeDialogOpen} onOpenChange={setAnalyzeDialogOpen}>
                  <DialogTrigger asChild><Button size="sm" className="bg-bronze hover:bg-bronze-light" data-testid="analyze-btn"><Sparkles className="w-4 h-4 mr-1" />AI Analysis</Button></DialogTrigger>
                  <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>AI Exhibit Analysis</DialogTitle>
                      <DialogDescription>Describe your case arguments to help AI identify relevant exhibits</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="flex items-center justify-between">
                        <Label>Case Arguments</Label>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setTemplatesDialogOpen(true)}
                          data-testid="use-template-btn"
                        >
                          <Lightbulb className="w-4 h-4 mr-1" />
                          Use Template
                        </Button>
                      </div>
                      <Textarea 
                        placeholder="Describe your client's position, key arguments, and what you need to prove. The more specific you are, the better the AI can identify relevant documents..." 
                        value={arguments_} 
                        onChange={(e) => setArguments(e.target.value)} 
                        rows={8} 
                        data-testid="arguments-input" 
                        className="font-mono text-sm"
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span><FileText className="w-4 h-4 inline mr-1" />{documents.length} documents will be analyzed</span>
                        {exhibits.length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-6"
                            onClick={() => { setAnalyzeDialogOpen(false); setComparisonDialogOpen(true); }}
                          >
                            <History className="w-3 h-3 mr-1" />
                            View Previous Analyses
                          </Button>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAnalyzeDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleAnalyze} disabled={analyzing || !arguments_.trim() || !documents.length} className="bg-bronze hover:bg-bronze-light" data-testid="run-analysis-btn">
                        {analyzing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Analyzing...</> : <><Sparkles className="w-4 h-4 mr-1" />Run Analysis</>}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}

            {isOwner && (
              <Dialog open={collabDialogOpen} onOpenChange={setCollabDialogOpen}>
                <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="share-btn"><Users className="w-4 h-4 mr-1" />Share</Button></DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Share Case</DialogTitle><DialogDescription>Invite collaborators</DialogDescription></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="flex gap-2">
                      <Input placeholder="Email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="flex-1" data-testid="invite-email-input" />
                      <Select value={inviteRole} onValueChange={setInviteRole}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="editor">Editor</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent></Select>
                      <Button onClick={handleInvite} disabled={inviting} className="bg-navy hover:bg-navy-light">{inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}</Button>
                    </div>
                    <div className="border-t pt-4"><p className="text-sm font-medium mb-2">Team</p><div className="space-y-2">{collaborators.map(c => (
                      <div key={c.user_id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <div className="flex items-center gap-2">{getRoleIcon(c.role)}<div><p className="text-sm font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></div></div>
                        {c.role !== 'owner' && <div className="flex items-center gap-1"><Select value={c.role} onValueChange={(v) => handleUpdateRole(c.user_id, v)}><SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="editor">Editor</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent></Select><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleRemoveCollaborator(c.user_id)}><X className="w-3 h-3" /></Button></div>}
                        {c.role === 'owner' && <Badge variant="secondary" className="text-xs">Owner</Badge>}
                      </div>
                    ))}</div></div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {finalExhibits.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownloadFinalList} data-testid="final-list-btn"><ListChecks className="w-4 h-4 mr-1" />Final List</Button>
            )}
            {exhibits.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDownloadReport} data-testid="download-report-btn"><Download className="w-4 h-4 mr-1" />Report</Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList>
            <TabsTrigger value="documents" data-testid="documents-tab"><FileText className="w-4 h-4 mr-2" />Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="exhibits" data-testid="exhibits-tab"><CheckCircle2 className="w-4 h-4 mr-2" />AI Exhibits ({exhibits.length})</TabsTrigger>
            <TabsTrigger value="final" data-testid="final-tab"><ListChecks className="w-4 h-4 mr-2" />Final Exhibits ({finalExhibits.length})</TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            {documents.length > 0 && canEdit && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant={bulkMode ? "secondary" : "outline"} size="sm" onClick={() => { setBulkMode(!bulkMode); setSelectedDocs(new Set()); }}>{bulkMode ? 'Cancel' : 'Bulk Select'}</Button>
                {bulkMode && <>
                  <Button variant="outline" size="sm" onClick={selectAllDocs}>{selectedDocs.size === documents.length ? 'Deselect All' : 'Select All'}</Button>
                  {selectedDocs.size > 0 && <>
                    <Button variant="outline" size="sm" onClick={() => setBulkDialogOpen(true)}><Tag className="w-4 h-4 mr-1" />Tag ({selectedDocs.size})</Button>
                    <Button variant="outline" size="sm" className="text-destructive" onClick={() => setBulkDeleteConfirm(true)}><Trash2 className="w-4 h-4 mr-1" />Delete ({selectedDocs.size})</Button>
                  </>}
                </>}
              </div>
            )}

            {documents.length > 0 ? (
              <div className="grid gap-3">
                {documents.map((doc, idx) => (
                  <Card key={doc.id} className={`border shadow-sm hover:border-bronze/30 transition-all ${doc.is_final_exhibit ? 'ring-2 ring-success/30 bg-success/5' : doc.is_exhibit ? 'ring-1 ring-bronze/30' : ''} ${selectedDocs.has(doc.id) ? 'ring-2 ring-bronze' : ''}`} data-testid={`document-${doc.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {bulkMode && <Checkbox checked={selectedDocs.has(doc.id)} onCheckedChange={() => toggleDocSelection(doc.id)} />}
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80" onClick={() => setPreviewDoc(doc)}>{getFileIcon(doc.file_type)}</div>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreviewDoc(doc)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium truncate">{doc.filename}</p>
                            {doc.exhibit_label && <Badge className="bg-bronze text-white text-xs">{doc.exhibit_label}</Badge>}
                            {doc.is_final_exhibit && <Badge className="bg-success text-white text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Final</Badge>}
                            {doc.is_exhibit && !doc.exhibit_label && <Badge variant="outline" className="text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />AI Pick</Badge>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="font-mono text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span>
                            {doc.category && <Badge variant="outline" className="text-xs">{doc.category}</Badge>}
                            {doc.tags?.slice(0,2).map((t,i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
                          </div>
                        </div>
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setPreviewDoc(doc)}><Eye className="w-4 h-4 mr-2" />Preview</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(doc)}><Edit className="w-4 h-4 mr-2" />Edit Tags</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openLabelDialog(doc)}><Hash className="w-4 h-4 mr-2" />Assign Exhibit Label</DropdownMenuItem>
                              {doc.exhibit_label && <DropdownMenuItem onClick={() => handleRemoveLabel(doc.id)}><X className="w-4 h-4 mr-2" />Remove Label</DropdownMenuItem>}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteDocument(doc.id)}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2"><CardContent className="p-12 text-center"><Upload className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg mb-2">No documents</h3><p className="text-muted-foreground mb-4">Upload documents to start</p>{canEdit && <Button onClick={() => setUploadDialogOpen(true)} className="bg-navy hover:bg-navy-light"><Upload className="w-4 h-4 mr-2" />Upload</Button>}</CardContent></Card>
            )}
          </TabsContent>

          {/* AI Exhibits Tab */}
          <TabsContent value="exhibits" className="space-y-4">
            {/* Report download button */}
            {exhibits.length > 0 && (
              <div className="flex justify-between items-center">
                <Button 
                  variant="outline"
                  onClick={() => setComparisonDialogOpen(true)}
                  data-testid="compare-analyses-btn"
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare Analyses
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDownloadAiIdentificationReport}
                  data-testid="download-ai-report-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download AI Identification Report
                </Button>
              </div>
            )}
            {exhibits.length > 0 ? (
              <div className="grid gap-4">
                {exhibits.map((ex, i) => (
                  <Card key={ex.id} className="border-bronze/30 bg-bronze/5 cursor-pointer hover:shadow-md" onClick={() => setPreviewDoc(ex)} data-testid={`exhibit-${ex.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-md bg-bronze/20 flex items-center justify-center flex-shrink-0"><span className="font-mono text-sm font-bold text-bronze">#{i+1}</span></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">{getFileIcon(ex.file_type)}<h4 className="font-semibold">{ex.filename}</h4>{ex.exhibit_relevance && <Badge className="bg-bronze text-white">{Math.round(ex.exhibit_relevance * 100)}%</Badge>}{ex.exhibit_label && <Badge variant="outline">{ex.exhibit_label}</Badge>}</div>
                          {ex.exhibit_reasoning && <p className="text-sm text-muted-foreground">{ex.exhibit_reasoning}</p>}
                        </div>
                        {canEdit && !ex.exhibit_label && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openLabelDialog(ex); }}><Hash className="w-4 h-4 mr-1" />Label</Button>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2"><CardContent className="p-12 text-center"><Sparkles className="w-12 h-12 mx-auto text-bronze/50 mb-4" /><h3 className="font-heading text-lg mb-2">No AI exhibits yet</h3><p className="text-muted-foreground mb-4">{documents.length ? 'Run AI analysis to identify exhibits' : 'Upload documents first'}</p>{canEdit && <Button onClick={() => setAnalyzeDialogOpen(true)} disabled={!documents.length} className="bg-bronze hover:bg-bronze-light"><Sparkles className="w-4 h-4 mr-2" />Run Analysis</Button>}</CardContent></Card>
            )}
          </TabsContent>

          {/* Final Exhibits Tab */}
          <TabsContent value="final" className="space-y-4">
            {/* Final Exhibits List */}
            {finalExhibits.length > 0 && <div className="flex justify-end"><Button variant="outline" onClick={handleDownloadFinalList}><ListChecks className="w-4 h-4 mr-2" />Download Exhibit List PDF</Button></div>}
            {finalExhibits.length > 0 ? (
              <div className="grid gap-4">
                {finalExhibits.map((ex) => (
                  <Card key={ex.id} className="border-success/30 bg-success/5" data-testid={`final-exhibit-${ex.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-md bg-success/20 flex items-center justify-center flex-shrink-0">
                          {ex.labeled_file_path ? (
                            <Stamp className="w-6 h-6 text-success" />
                          ) : (
                            <CheckCircle2 className="w-6 h-6 text-success" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {getFileIcon(ex.file_type)}
                            <h4 className="font-semibold">{ex.filename}</h4>
                            <Badge className="bg-success text-white">{ex.exhibit_label}</Badge>
                            {ex.labeled_file_path && (
                              <Badge variant="outline" className="text-xs border-success text-success">
                                <Stamp className="w-3 h-3 mr-1" />Stamped
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{ex.category || 'Uncategorized'}</span>
                            {ex.label_position && (
                              <span className="text-xs">• Label: {ex.label_position.replace('-', ' ')}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setPreviewDoc(ex)}>
                            <Eye className="w-4 h-4 mr-1" />Preview
                          </Button>
                          {ex.labeled_file_path && (
                            <Button size="sm" variant="outline" onClick={() => handleDownloadLabeledDocument(ex.id, ex.filename)}>
                              <FileDown className="w-4 h-4 mr-1" />Download
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2">
                <CardContent className="p-12 text-center">
                  <ListChecks className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-heading text-lg mb-2">No final exhibits</h3>
                  <p className="text-muted-foreground">Assign exhibit labels to documents to add them to your final exhibit list.</p>
                  <p className="text-muted-foreground mt-2 text-sm">Enable "Stamp label on document" when assigning to add the label directly to the PDF.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Document Preview */}
      {previewDoc && <DocumentPreview caseId={id} document={previewDoc} onClose={() => setPreviewDoc(null)} />}

      {/* Edit Tags Dialog */}
      <Dialog open={!!editDocDialog} onOpenChange={(o) => !o && setEditDocDialog(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Category</Label><Select value={editCategory} onValueChange={setEditCategory}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Tags (comma-separated)</Label><Input value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="important, reviewed" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditDocDialog(null)}>Cancel</Button><Button onClick={handleUpdateDocument} className="bg-navy hover:bg-navy-light">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>Bulk Edit {selectedDocs.size} Documents</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Category</Label><Select value={bulkCategory} onValueChange={setBulkCategory}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Tags</Label><Input value={bulkTags} onChange={(e) => setBulkTags(e.target.value)} placeholder="tag1, tag2" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button><Button onClick={handleBulkUpdate} className="bg-navy hover:bg-navy-light">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete {selectedDocs.size} documents?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exhibit Label Dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Exhibit Label</DialogTitle>
            <DialogDescription>Label document: {labelDoc?.filename}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Party</Label>
              <Select value={labelParty} onValueChange={setLabelParty}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EXHIBIT_PARTIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Numbering Style</Label>
              <Select value={labelNumbering} onValueChange={setLabelNumbering}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="numeric">Numeric (1, 2, 3...)</SelectItem>
                  <SelectItem value="alpha">Alphabetic (A, B, C... AA, AB...)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Number (leave blank for auto)</Label>
              <Input value={labelNumber} onChange={(e) => setLabelNumber(e.target.value)} placeholder={labelNumbering === 'numeric' ? 'e.g., 1' : 'e.g., A or AA'} />
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium mb-1">Preview:</p>
              <p className="text-lg font-semibold text-bronze">{labelParty}'s Exhibit {labelNumber || '(auto)'}</p>
            </div>

            {/* Stamp Label Option - only for PDFs and images */}
            {labelDoc && (labelDoc.file_type === 'pdf' || labelDoc.file_type === 'image') && (
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="stamp-label" 
                    checked={stampLabel} 
                    onCheckedChange={setStampLabel}
                    data-testid="stamp-label-checkbox"
                  />
                  <Label htmlFor="stamp-label" className="text-sm font-medium cursor-pointer">
                    Stamp label on document
                  </Label>
                </div>
                
                {stampLabel && (
                  <div className="space-y-2 pl-6">
                    <Label className="text-sm text-muted-foreground">Label Position</Label>
                    <Select value={labelPosition} onValueChange={setLabelPosition}>
                      <SelectTrigger data-testid="label-position-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LABEL_POSITIONS.map(pos => (
                          <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The exhibit label will be permanently added to the first page of the document.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelDialogOpen(false)} disabled={labeling}>Cancel</Button>
            <Button onClick={handleAssignLabel} disabled={labeling} className="bg-bronze hover:bg-bronze-light" data-testid="assign-label-btn">
              {labeling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Labeling...</>
              ) : (
                'Assign Label'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analysis Comparison Dialog */}
      <AnalysisComparison 
        caseId={id} 
        isOpen={comparisonDialogOpen} 
        onClose={() => setComparisonDialogOpen(false)} 
      />

      {/* Argument Templates Dialog */}
      <ArgumentTemplates 
        isOpen={templatesDialogOpen} 
        onClose={() => setTemplatesDialogOpen(false)}
        onSelectTemplate={(template) => setArguments(template)}
      />
    </Layout>
  );
}
